import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    const mapping = this.querySelector('[data-media-variant-map]');
    if (mapping) {
      this.#variantMap = JSON.parse(mapping.textContent);
      this.addEventListener('slideshow:select', (event) => {
        if (event.target === this.slideshow && event.detail.userInitiated) {
          this.#selectImageVariant(event.detail.index);
        }
      }, { signal });
      this.addEventListener('click', (event) => {
        const gridMedia = event.target.closest('li[ref="media[]"]');
        if (gridMedia && !gridMedia.closest('zoom-dialog')) {
          const items = Array.from(gridMedia.parentElement.children);
          this.#selectImageVariant(items.indexOf(gridMedia));
        }
      }, { signal });
    }

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #variantMap;
  #requestedVariantId;

  #selectImageVariant(index) {
    const mapping = this.#variantMap;
    if (!mapping) return;
    const mediaId = mapping.mediaIds[index];
    const linked = mapping.variants.filter((variant) => variant.mediaId === mediaId && mediaId != null);
    // An image shared across different colors cannot identify a unique color.
    const colors = new Set(linked.map((variant) => variant.options[mapping.colorIndex]));
    if (colors.size !== 1) return;
    const color = linked[0].options[mapping.colorIndex];
    const picker = this.closest('.shopify-section')?.querySelector(
      `variant-picker[data-product-id="${mapping.productId}"][data-template-product-match="true"]`
    );
    if (!picker?.selectVariantFromMedia) return;
    const current = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null');
    const currentOptions = current?.options ?? [];
    const candidates = mapping.variants.filter((variant) =>
      variant.available && variant.options[mapping.colorIndex] === color
    );
    // Keep size and other options when possible; otherwise choose the closest available combination.
    const score = (variant) => variant.options.reduce((total, option, optionIndex) =>
      total + (optionIndex !== mapping.colorIndex && option === currentOptions[optionIndex] ? 1 : 0), 0);
    candidates.sort((a, b) => score(b) - score(a));
    const variant = candidates[0];
    if (!variant || variant.id === (this.#requestedVariantId ?? current?.id)) return;
    this.#requestedVariantId = variant.id;
    picker.selectVariantFromMedia(variant.id);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
